import { useMemo } from "react"
import { type MetricConfiguration } from "utils/trpc"

export type MetricSortingConfig = {
  // Define custom sort order for metrics within a category
  categoryMetricOrder?: Record<string, string[]>
  // Define custom sort order for categories
  categoryOrder?: string[]
  // Default sort function for metrics without explicit ordering
  defaultMetricSort?: (
    a: MetricConfiguration,
    b: MetricConfiguration,
    metricsConfigurationListDic: Map<string, any>
  ) => number
}

/**
 * Hook for sorting metrics by category with customizable ordering
 */
export function useMetricSorting(
  metricsByCategory: Record<string, MetricConfiguration[]>,
  metricsConfigurationListDic: Map<string, MetricConfiguration>,
  config: MetricSortingConfig = {}
) {
  const {
    categoryMetricOrder = {},
    categoryOrder = [
      "market",
      "financial",
      "valuation",
      "usage",
      "development",
      "technical",
      "ecosystem",
      "custom",
      "other",
    ],
    defaultMetricSort = (a, b, metricsConfigurationListDic) => {
      const titleA =
        metricsConfigurationListDic.get(a.metric_id)?.title || a.title || ""
      const titleB =
        metricsConfigurationListDic.get(b.metric_id)?.title || b.title || ""
      return titleA.localeCompare(titleB)
    },
  } = config

  // Sort metrics within each category
  const sortedMetricsByCategory: Record<string, MetricConfiguration[]> =
    useMemo(() => {
      let result = {
        ...metricsByCategory,
      }

      if (metricsByCategory["financial"]) {
        result = {
          ...result,
          financial: result["financial"].filter(
            (metric) => metric.metric_id !== "net_deposits"
          ),
        }
      }

      Object.keys(result).forEach((category) => {
        const categoryLower = category.toLowerCase()
        const metricOrder = categoryMetricOrder[categoryLower] || []

        if (metricOrder.length > 0) {
          // If we have a custom order for this category, use it
          result[category].sort((a, b) => {
            const indexA = metricOrder.indexOf(a.metric_id)
            const indexB = metricOrder.indexOf(b.metric_id)

            // If both metrics are in the custom order, sort by that order
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB
            }

            // If only one metric is in the custom order, prioritize it
            if (indexA !== -1) return -1
            if (indexB !== -1) return 1

            // If neither metric is in the custom order, use the default sort
            return defaultMetricSort(a, b, metricsConfigurationListDic)
          })
        } else {
          // Otherwise use the default alphabetical sort
          result[category].sort((a, b) =>
            defaultMetricSort(a, b, metricsConfigurationListDic)
          )
        }
      })

      return result
    }, [
      metricsByCategory,
      categoryMetricOrder,
      defaultMetricSort,
      metricsConfigurationListDic,
    ])

  // Sort categories
  const sortedCategories = useMemo(() => {
    return Object.keys(sortedMetricsByCategory)
      .filter((category) => sortedMetricsByCategory[category].length > 0)
      .sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.toLowerCase())
        const bIndex = categoryOrder.indexOf(b.toLowerCase())

        // If both categories are in the custom order, sort by that order
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex
        }

        // If only one category is in the custom order, prioritize it
        if (aIndex !== -1) return -1
        if (bIndex !== -1) return 1

        // If neither category is in the custom order, sort alphabetically
        return a.localeCompare(b)
      })
  }, [sortedMetricsByCategory, categoryOrder])

  return {
    sortedMetricsByCategory,
    sortedCategories,
  }
}
